import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  MenuItem,
  CircularProgress,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Select,
  InputLabel,
  Divider,
  useTheme,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/axios';

const steps = ['Account Type', 'Basic Info', 'Profile Details'];

const Register = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    userType: 'dj',
    // DJ specific fields
    genre: '',
    instagram: '',
    twitter: '',
    facebook: '',
    soundcloud: '',
    website: '',
    // Venue specific fields
    contactName: '',
    phone: '',
    venueWebsite: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA',
    venueType: '',
    capacity: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const {
    name, email, password, userType, genre,
    instagram, twitter, facebook, soundcloud, website,
    contactName, phone, venueWebsite, street, city, state, zip, country, venueType, capacity
  } = formData;

  // Clear fields when userType changes
  useEffect(() => {
    if (userType === 'dj') {
      setFormData(prevState => ({
        ...prevState,
        contactName: '', phone: '', venueWebsite: '', street: '', city: '',
        state: '', zip: '', country: 'USA', venueType: '', capacity: '',
      }));
    } else {
      setFormData(prevState => ({ ...prevState, genre: '' }));
    }
  }, [userType]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0 && !userType) {
      setError('Please select an account type.');
      return;
    }
    if (activeStep === 1) {
      if (!name || !email || !password) {
        setError('Please fill in all required fields.');
        return;
      }
    }
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!name || !email || !password || !userType) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    const registrationData = { name, email, password, userType };

    if (userType === 'dj') {
      registrationData.genre = genre || undefined;
      registrationData.socialLinks = {
        instagram: instagram || undefined,
        twitter: twitter || undefined,
        facebook: facebook || undefined,
        soundcloud: soundcloud || undefined,
        website: website || undefined,
      };
    }

    if (userType === 'venue') {
      registrationData.contactName = contactName || undefined;
      registrationData.phone = phone || undefined;
      registrationData.venueWebsite = venueWebsite || undefined;
      registrationData.street = street || undefined;
      registrationData.city = city || undefined;
      registrationData.state = state || undefined;
      registrationData.zip = zip || undefined;
      registrationData.country = country || undefined;
      registrationData.venueType = venueType || undefined;
      registrationData.capacity = capacity || undefined;
    }

    try {
      const response = await api.post('/users/register', registrationData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      if (response.data.user.userType === 'dj') {
        navigate('/dj/dashboard');
      } else if (response.data.user.userType === 'venue') {
        navigate('/subscription/plans');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Registration error details:', err.response?.data);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Lists for dropdowns
  const venueTypes = ['Nightclub', 'Bar', 'Lounge', 'Event Space', 'Restaurant', 'Other'];

  const djGenres = [
    'House', 'Techno', 'EDM', 'Hip Hop', 'R&B', 'Top 40', 'Country', 'Open Format',
    'Trance', 'Dubstep', 'Drum & Bass', 'Latin', 'Reggaeton', 'Disco',
    'Funk', 'Soul', 'Jazz', 'Rock', 'Pop', 'Other',
  ];

  const usStates = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
    { code: 'DC', name: 'District of Columbia' },
  ];

  // --- Step renderers ---

  const renderStep0 = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
        I am a...
      </Typography>
      <RadioGroup
        row
        aria-label="userType"
        name="userType"
        value={userType}
        onChange={handleChange}
        sx={{ justifyContent: 'center' }}
      >
        <FormControlLabel
          value="dj"
          control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />}
          label={
            <Typography variant="h6" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '0.05em' }}>
              DJ
            </Typography>
          }
          sx={{ mx: 4, p: 2, border: '2px solid', borderColor: userType === 'dj' ? 'primary.main' : 'secondary.light', transition: 'all 0.2s' }}
        />
        <FormControlLabel
          value="venue"
          control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />}
          label={
            <Typography variant="h6" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '0.05em' }}>
              Venue
            </Typography>
          }
          sx={{ mx: 4, p: 2, border: '2px solid', borderColor: userType === 'venue' ? 'primary.main' : 'secondary.light', transition: 'all 0.2s' }}
        />
      </RadioGroup>
    </Box>
  );

  const renderStep1 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          required fullWidth id="name"
          label={userType === 'venue' ? 'Venue Name' : 'Name / DJ Name'}
          name="name" autoComplete="name" autoFocus
          value={name} onChange={handleChange} disabled={loading}
          variant="outlined" InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          required fullWidth id="email" label="Email Address"
          name="email" type="email" autoComplete="email"
          value={email} onChange={handleChange} disabled={loading}
          variant="outlined" InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required fullWidth name="password" label="Password"
          type="password" id="password" autoComplete="new-password"
          value={password} onChange={handleChange} disabled={loading}
          variant="outlined" InputLabelProps={{ shrink: true }}
        />
      </Grid>
    </Grid>
  );

  const renderStep2DJ = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="genre-label" shrink>Primary Genre</InputLabel>
          <Select
            labelId="genre-label" id="genre" name="genre" value={genre}
            label="Primary Genre" onChange={handleChange} disabled={loading}
            notched
            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
          >
            <MenuItem value=""><em>Select Genre...</em></MenuItem>
            {djGenres.map((g) => (<MenuItem key={g} value={g}>{g}</MenuItem>))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Social Media Links (Optional)</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth id="instagram" label="Instagram Username" name="instagram"
          value={instagram} onChange={handleChange} disabled={loading} variant="outlined"
          placeholder="username (without @)" InputLabelProps={{ shrink: true }} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth id="twitter" label="Twitter/X Username" name="twitter"
          value={twitter} onChange={handleChange} disabled={loading} variant="outlined"
          placeholder="username (without @)" InputLabelProps={{ shrink: true }} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth id="facebook" label="Facebook URL" name="facebook" type="url"
          value={facebook} onChange={handleChange} disabled={loading} variant="outlined"
          placeholder="https://facebook.com/..." InputLabelProps={{ shrink: true }} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth id="soundcloud" label="Soundcloud URL" name="soundcloud" type="url"
          value={soundcloud} onChange={handleChange} disabled={loading} variant="outlined"
          placeholder="https://soundcloud.com/..." InputLabelProps={{ shrink: true }} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth id="website" label="Personal Website" name="website" type="url"
          value={website} onChange={handleChange} disabled={loading} variant="outlined"
          placeholder="https://..." InputLabelProps={{ shrink: true }} />
      </Grid>
    </Grid>
  );

  const renderStep2Venue = () => (
    <>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>Venue Details</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth id="contactName" label="Contact Person" name="contactName"
            autoComplete="name" value={contactName} onChange={handleChange} disabled={loading}
            variant="outlined" InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth id="phone" label="Contact Phone Number" name="phone"
            autoComplete="tel" value={phone} onChange={handleChange} disabled={loading}
            variant="outlined" InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth id="venueWebsite" label="Website (Optional)" name="venueWebsite"
            autoComplete="url" value={venueWebsite} onChange={handleChange} disabled={loading}
            variant="outlined" InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="venue-type-label" shrink>Venue Type</InputLabel>
            <Select labelId="venue-type-label" id="venueType" name="venueType"
              value={venueType} label="Venue Type" onChange={handleChange} disabled={loading} notched
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}>
              <MenuItem value=""><em>Select Type...</em></MenuItem>
              {venueTypes.map((type) => (<MenuItem key={type} value={type}>{type}</MenuItem>))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth id="capacity" label="Approximate Capacity" name="capacity"
            type="number" value={capacity} onChange={handleChange}
            InputProps={{ inputProps: { min: 0 } }} disabled={loading}
            variant="outlined" InputLabelProps={{ shrink: true }} />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>Venue Address</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField fullWidth id="street" label="Street Address" name="street"
            autoComplete="street-address" value={street} onChange={handleChange}
            disabled={loading} variant="outlined" InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth id="city" label="City" name="city"
            autoComplete="address-level2" value={city} onChange={handleChange}
            disabled={loading} variant="outlined" InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="state-label" shrink>State</InputLabel>
            <Select labelId="state-label" id="state" name="state" value={state}
              label="State" onChange={handleChange} disabled={loading} notched
              renderValue={(selected) => {
                if (!selected) return <em>Select State...</em>;
                const s = usStates.find(st => st.code === selected);
                return s ? `${s.code} - ${s.name}` : selected;
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}>
              <MenuItem value=""><em>Select State...</em></MenuItem>
              {usStates.map((s) => (
                <MenuItem key={s.code} value={s.code}>{s.code} - {s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth id="zip" label="Zip / Postal Code" name="zip"
            autoComplete="postal-code" value={zip} onChange={handleChange}
            disabled={loading} variant="outlined" InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth id="country" label="Country" name="country"
            autoComplete="country-name" value={country} onChange={handleChange}
            disabled={loading} variant="outlined" InputLabelProps={{ shrink: true }} />
        </Grid>
      </Grid>
    </>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return userType === 'dj' ? renderStep2DJ() : renderStep2Venue();
      default: return null;
    }
  };

  return (
    <Container component="main" maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ marginY: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper
          elevation={3}
          sx={{ p: { xs: 3, md: 5 }, width: '100%', borderRadius: 2 }}
        >
          <Typography
            component="h1" variant="h4" align="center" gutterBottom
            sx={{
              fontWeight: 600, mb: 4,
              background: (t) => `linear-gradient(45deg, ${t.palette.primary.main} 30%, ${t.palette.secondary.main} 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Create your SetSync account
          </Typography>

          {/* Stepper */}
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              mb: 4,
              '& .MuiStepLabel-label': { fontFamily: '"Space Mono", monospace', fontSize: '0.75rem', letterSpacing: '0.05em' },
              '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
              '& .MuiStepIcon-root.Mui-completed': { color: 'primary.main' },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>

              {activeStep < steps.length - 1 ? (
                <Button variant="contained" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button type="submit" variant="contained" disabled={loading}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'SIGN UP'}
                </Button>
              )}
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2">
                Already have an account?{' '}
                <Link to="/login" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                  Sign in
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
